
import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography } from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';

import { TablePaginationActions } from '../helpers/TablePaginationActions';

import { usePages } from '../hooks/usePages';

import { openSnackbar } from "../store/slices/SnackbarSlice";

import { TableTheadStyle, TablePaddingStyle } from '../styles/TableStyle';
import { DialogActionsStyle, DialogContentStyle } from '../styles/DialogStyle';

import { ObtenerTipoPrestaciones } from '../connections/catalogos/TipoPrestacionConnection';
import { ObtenerPrestacionesExpediente, AgregarPrestacionesExpediente } from '../connections/recepcion/ExpedienteConnection';

import { TipoPrestacionProps } from '../interfaces/catalogos/TipoPrestacionInterface';

interface Props {
    openDialog:         boolean;
    handleCloseDialog:  () => void;
    idExpediente?:      number;
    idTipoJuicio?:      number;
    onGuardar?:         ( prestaciones: string[], medioPreparatorio: boolean ) => void;
}

export const DialogPrestacionesExpediente = ( { openDialog, handleCloseDialog, idExpediente = 0, idTipoJuicio = 0, onGuardar }: Props ) => {

    const dispatch = useDispatch();

    const [tipoPrestacion, setTipoPrestacion] = useState( 0 );
    const [tipoPrestacionArray, setTipoPrestacionArray] = useState<TipoPrestacionProps[]>( [] );

    const [loading, setLoading] = useState( true );
    const [loadingGuardar, setLoadingGuardar] = useState( false );
    const [openConfirmacion, setOpenConfirmacion] = useState( false );

    const [idReferencia, setIdReferencia] = useState( 0 );

    const [rows, setRows] = useState<TipoPrestacionProps[]>( [] );

    const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePages( 5 );

    const handleAgregar = () => {

        if( tipoPrestacion === 0 ) return;

        const yaExiste = rows.find( (r) => r.idTipoPrestacion === tipoPrestacion );

        if( yaExiste ){

            dispatch( openSnackbar({ message: 'La prestación ya se encuentra agregada', variant: 'warning' }) );
            return;

        }

        if( idTipoJuicio === 1 || idTipoJuicio === 3 ){

            if( tipoPrestacion !== 12 ){

                const mediosPreparatorios = rows.filter( (r) => r.idTipoPrestacion === 12 );

                if( mediosPreparatorios.length !== 0 ){

                    dispatch( openSnackbar({ message: 'El concepto seleccionado no puede agregarse en conjunto con el concepto de Medios Preparatorios', variant: 'warning' }) );
                    setTipoPrestacion( 0 );

                    return;
                }

            } 
            else {

                const otrosConceptos = rows.filter( (r) => r.idTipoPrestacion !== 12 );

                if( otrosConceptos.length !== 0 ){

                    dispatch( openSnackbar({ message: 'El concepto de Medios Preparatorios no puede agregarse en conjunto con los conceptos: ' + otrosConceptos.map( (e) => e.descripcion ).join(', '), variant: 'warning' }) );
                    setTipoPrestacion( 0 );

                    return;
                }

            }

        }

        const data = tipoPrestacionArray.find( (t) => t.idTipoPrestacion === tipoPrestacion );

        if( data ){
            setRows( [...rows, data] );
        }

        setTipoPrestacion( 0 );

    }

    const handleRemover = () => {

        setRows( rows.filter( (r) => r.idTipoPrestacion !== idReferencia ) );
        setOpenConfirmacion( false );

    }

    const handleGuardar = async () => {

        setLoadingGuardar( true );

        await AgregarPrestacionesExpediente({ idExpediente, prestaciones: rows }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if( success ){

                    const mediosPreparatorios = rows.filter( (r) => r.idTipoPrestacion === 12 );

                    dispatch( openSnackbar({ message, variant: 'success' }) );
                    setLoadingGuardar( false );

                    handleCloseDialog();

                    onGuardar?.( rows.map( (r) => r.descripcion ), mediosPreparatorios.length !== 0 );

                } else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );
                    setLoadingGuardar( false );

                }

            }, 700);

        });

    }

    useEffect(() => {

        async function Obtener(){

            await ObtenerTipoPrestaciones().then( resp => {

                setTipoPrestacionArray( resp );

            });

        }

        Obtener();

    }, []);

    useEffect(() => {

        async function Obtener(){

            setLoading( true );
            setRows( [] );

            await ObtenerPrestacionesExpediente( idExpediente ).then( resp => {

                setTimeout(() => {

                    setRows( resp );
                    setLoading( false );

                }, 700);

            });

        }

        if( idExpediente !== 0 && openDialog ){

            Obtener();

        } 
        else {
            setLoading( false );
        }

    }, [ openDialog, idExpediente ]);

    return (

        <>

            <Dialog
                fullWidth
                open={ openDialog }
                maxWidth='md'
            >

                <DialogTitle>
                    Agregar prestación(es) o pretención(es)
                </DialogTitle>

                <DialogContent>

                    <Grid container spacing={3} mt={1}>

                        <Grid size={{ xs: 12 }}>

                            <FormControl fullWidth>

                                <InputLabel>Tipo de prestación</InputLabel>

                                <Select
                                    label="Tipo de prestación"
                                    value={ tipoPrestacion }
                                    onChange={ (e) => setTipoPrestacion( e.target.value as number ) }
                                >
                                    <MenuItem value={0}>Selecciona una opción</MenuItem>

                                    {
                                        ( idTipoJuicio === 1 || idTipoJuicio === 3 )
                                        ?
                                            tipoPrestacionArray
                                            .map( (t) => (
                                                <MenuItem key={ t.idTipoPrestacion } value={ t.idTipoPrestacion }>{ t.descripcion }</MenuItem>
                                            ))
                                        :
                                            tipoPrestacionArray
                                            .filter( ({ idTipoPrestacion }) => idTipoPrestacion !== 12 )
                                            .map( (t) => (
                                                <MenuItem key={ t.idTipoPrestacion } value={ t.idTipoPrestacion }>{ t.descripcion }</MenuItem>
                                            ))
                                    }

                                </Select>

                            </FormControl>

                        </Grid>

                        <Grid size={{ xs: 12 }} textAlign='end'>

                            <Button
                                variant="contained"
                                onClick={ handleAgregar }
                                disabled={ tipoPrestacion === 0 }
                            >
                                Agregar
                            </Button>

                        </Grid>

                        <Grid size={{ xs: 12 }}>

                            <TableContainer sx={{ borderRadius: 3, mt: 1 }}>

                                <Table sx={{ minWidth: 400 }}>

                                    <TableHead sx={{ ...TableTheadStyle }}>

                                        <TableRow>

                                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '80%' }}>Prestación / Pretención</TableCell>
                                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '20%' }}> </TableCell>

                                        </TableRow>

                                    </TableHead>

                                    <TableBody>

                                        {
                                            (
                                                rowsPerPage > 0
                                                ? rows.slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage )
                                                : rows
                                            ).map( ( { idTipoPrestacion, descripcion }, index ) => (

                                                <TableRow key={ index }>

                                                    <TableCell>{ descripcion }</TableCell>

                                                    <TableCell sx={{ textAlign: 'end' }}>

                                                        <IconButton color="primary" onClick={ () => { setIdReferencia( idTipoPrestacion ); setOpenConfirmacion( true ); } }>
                                                            <DeleteIcon />
                                                        </IconButton>

                                                    </TableCell>

                                                </TableRow>

                                            ))
                                        }

                                        {
                                            ( rows.length === 0 && !loading )
                                            ?
                                                <TableRow style={{ height: 73 }}>

                                                    <TableCell colSpan={2} align="center">
                                                        <Typography variant="caption" color='secondary'>No se agregaron prestaciones</Typography>
                                                    </TableCell>

                                                </TableRow>
                                            :
                                                loading
                                                &&
                                                    <TableRow style={{ height: 73 }}>

                                                        <TableCell colSpan={2} align="center">
                                                            <CircularProgress size={32} />
                                                        </TableCell>

                                                    </TableRow>
                                        }

                                    </TableBody>

                                    <TableFooter>

                                        <TableRow>

                                            <TablePagination
                                                rowsPerPageOptions={[]}
                                                colSpan={2}
                                                count={ rows.length }
                                                rowsPerPage={ rowsPerPage }
                                                page={ page }
                                                onPageChange={ handleChangePage }
                                                onRowsPerPageChange={ handleChangeRowsPerPage }
                                                ActionsComponent={ TablePaginationActions }
                                                labelDisplayedRows={
                                                    ({ from, to, count }) => `${ from }-${ to } de ${ count }`
                                                }
                                            />

                                        </TableRow>

                                    </TableFooter>

                                </Table>

                            </TableContainer>

                        </Grid>

                    </Grid>

                </DialogContent>

                <DialogActions>

                    <Button color='secondary' disabled={ loadingGuardar } onClick={ handleCloseDialog }>Cancelar</Button>

                    <Button
                        variant="contained"
                        loading={ loadingGuardar }
                        onClick={ handleGuardar }
                    >
                        Guardar
                    </Button>

                </DialogActions>

            </Dialog>

            <Dialog
                maxWidth='xs'
                fullWidth
                disableEscapeKeyDown
                open={ openConfirmacion }
                onClose={ () => {} }
            >

                <DialogTitle>
                    Confirmación
                </DialogTitle>

                <DialogContent sx={ DialogContentStyle }>

                    <DialogContentText sx={{ color: 'rgba(0, 0, 0, 0.8)' }} mt={1}>
                        ¿Desea quitar el registro seleccionado?
                    </DialogContentText>

                </DialogContent>

                <DialogActions sx={ DialogActionsStyle }>

                    <Button color='secondary' onClick={ () => setOpenConfirmacion( false ) }>Cancelar</Button>
                    <Button onClick={ handleRemover } variant='contained'>Aceptar</Button>

                </DialogActions>

            </Dialog>

        </>

    )

}
